// utils/eventHelpers.js
// PERF: moved out of components/EventDetail.js. Home.js and PayPalPayment.js
// imported these from EventDetail.js, which dragged the whole 6,700-line
// EventDetail page (plus ChatModal, Profile, PayPalPayment...) into the
// initial bundle and made code-splitting impossible. EventDetail.js still
// re-exports them, so any other import path keeps working.
import { db } from "../firebase";
import { collection, query, where, getDocs, documentId } from "firebase/firestore";

/**
 * Returns the effective base price for an event, factoring in early bird pricing.
 * If early bird is enabled and now < startDate - cutoffHours, returns early bird price.
 * Exported so PayPalPayment.js can use the same logic.
 */
export function getEarlyBirdStatus(event) {
  if (!event) return { effectivePrice: 0, isEarlyBird: false, earlyBirdEndsAt: null, savings: 0 };
  // Free events (event.isFreeEvent) cost nothing, early bird or not.
  if (event.isFreeEvent === true) return { effectivePrice: 0, isEarlyBird: false, earlyBirdEndsAt: null, savings: 0 };
  const normalPrice = event.price || 0;
  const eb = event.earlyBird;
  if (!eb?.enabled || eb.price == null) return { effectivePrice: normalPrice, isEarlyBird: false, earlyBirdEndsAt: null, savings: 0 };

  const cutoffMs = (eb.cutoffHours || 24) * 60 * 60 * 1000;
  const startDate = new Date(event.startDate);
  const earlyBirdEndsAt = new Date(startDate.getTime() - cutoffMs);
  const now = new Date();
  const isEarlyBird = now < earlyBirdEndsAt;

  return {
    effectivePrice: isEarlyBird ? eb.price : normalPrice,
    isEarlyBird,
    earlyBirdEndsAt,
    savings: isEarlyBird ? Math.round((normalPrice - eb.price) * 100) / 100 : 0,
  };
}

/**
 * Fetches user avatars in batches, with a process-wide cache.
 *
 * WHY THIS EXISTS
 * Both the home feed and this page used to fetch avatars with one getDoc per
 * user id, all fired at once via Promise.all and with no cache. A home feed
 * showing 40 events with 20 players each meant hundreds of simultaneous
 * document reads, repeated on every remount, for pictures that essentially
 * never change. On a phone that is enough to exhaust the tab's memory and get
 * it killed and reloaded by the browser.
 *
 * Three fixes in one:
 *   - `documentId() in [...]` fetches ten users per query instead of one
 *   - a module-level cache means a uid is only ever fetched once per session
 *   - at most three queries run concurrently, bounding peak memory
 */
const avatarCache = new Map();

export async function fetchUserAvatars(uids, nameFallbacks = {}) {
  const wanted = [...new Set(uids)].filter(Boolean);
  const result = {};
  const missing = [];

  wanted.forEach((uid) => {
    if (avatarCache.has(uid)) result[uid] = avatarCache.get(uid);
    else missing.push(uid);
  });

  const chunks = [];
  for (let i = 0; i < missing.length; i += 10) chunks.push(missing.slice(i, i + 10));

  let cursor = 0;
  const workers = Array.from({ length: Math.min(3, chunks.length) }, async () => {
    while (cursor < chunks.length) {
      const chunk = chunks[cursor++];
      try {
        const snap = await getDocs(
          query(collection(db, "users"), where(documentId(), "in", chunk))
        );
        const seen = new Set();
        snap.forEach((d) => {
          const entry = {
            photoURL: d.data().photoURL || null,
            name: d.data().name || nameFallbacks[d.id] || "",
          };
          avatarCache.set(d.id, entry);
          result[d.id] = entry;
          seen.add(d.id);
        });
        // Cache misses too, so a deleted user is not re-queried every render.
        chunk.filter((u) => !seen.has(u)).forEach((u) => {
          const entry = { photoURL: null, name: nameFallbacks[u] || "" };
          avatarCache.set(u, entry);
          result[u] = entry;
        });
      } catch (e) {
        chunk.forEach((u) => {
          result[u] = { photoURL: null, name: nameFallbacks[u] || "" };
        });
      }
    }
  });
  await Promise.all(workers);

  return result;
}

/**
 * Single source of truth for every capacity number in the UI.
 *
 * Exported from here (rather than a separate utils module) because Home.js
 * already imports getEarlyBirdStatus from this file, so no new file is needed.
 *
 * WHY IT EXISTS
 * Four different formulas were previously in use across Home.js and this file
 * and they disagreed with each other:
 *   Home.js card      currentParticipants + reservedForPromotion   ("joined")
 *   Home.js facepile  currentParticipants
 *   Home.js spotsLeft max - current - reserved
 *   Home.js admin     currentParticipants
 *
 * Worse, a player promoted from the waitlist who has not paid yet was counted
 * TWICE: once inside reservedForPromotion, and again because they are still
 * sitting in the event.waitlist array. A full event with one promoted player
 * and two genuinely waiting players read as "21/20 joined · +3 waitlisted"
 * instead of "20/20 · +2".
 *
 * reservedForPromotion is clamped at 0 here so a corrupted event document can
 * never render as free capacity.
 */
export function getEventCounts(event) {
  const max = event?.maxParticipants || 0;
  const taken = event?.currentParticipants || 0;
  const reserved = Math.max(0, event?.reservedForPromotion || 0);
  const waitlistSize = (event?.waitlist || []).length;

  return {
    max,
    // Confirmed, paid players only. Use for facepiles and revenue.
    joined: taken,
    // Confirmed + spots held by promoted-but-unpaid players. This is the
    // number to show as "X/Y" and to gate registration on, because a reserved
    // spot is not available to anyone else.
    occupied: taken + reserved,
    // Players still genuinely waiting: the array minus those already holding
    // a reserved spot (who are counted in `occupied`).
    waiting: Math.max(0, waitlistSize - reserved),
    // Raw array length. Only for admin tooling that needs the true size.
    waitlistSize,
    spotsLeft: Math.max(0, max - taken - reserved),
    isFull: max > 0 && (taken + reserved) >= max,
  };
}
